// app/javascript/controllers/customizable_text_controller.js
// import Quill from "quill"; // Globally imported
import ApplicationController from "./application_controller";

export default class CustomizableTextController extends ApplicationController {
  static targets = ["text", "allFieldsContainer", "editorContainer", 'displayContainer', "quillEditor", "editBtn"];

  connect() {
    this.textKey = this.element.dataset['key'];
    
    var toolbarOptions = [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],      // Headings
      ['bold', 'italic', 'underline', 'strike'],      // Formatting
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub'}, { 'script': 'super' }],    // Subscript/superscript
      [{ 'list': 'ordered'}, { 'list': 'bullet' }], // Lists
      [{ 'indent': '-1'}, { 'indent': '+1' }],       // Indentation
      [{ 'direction': 'rtl' }],                      // Text direction
      [{ 'align': ['', 'center', 'right', 'justify'] }], // Text alignment
      ['link', 'image', 'video'],                    // Links, images, and videos
      ['code-block', 'blockquote'],                  // Code and quotes
      ['clean']                                       // Remove formatting
    ];

    this.editor = new Quill(this.quillEditorTarget, {
      theme: "snow",
      placeholder: "Enter text here...",
      modules: {
        toolbar: toolbarOptions
      },
    });

    // Custom handler for text alignment
    this.setupAlignmentHandler();
  }

  setupAlignmentHandler() {
    // Handle text alignment changes
    this.editor.on('text-change', () => {
      const format = this.editor.getFormat();
      if (format.align) {
        // Apply alignment to the text target
        this.applyTextAlignment(format.align);
      }
    });
  }

  applyTextAlignment(alignment) {
    // Remove existing alignment classes
    this.textTarget.classList.remove('text-start', 'text-center', 'text-end', 'text-justify');
    
    // Add new alignment class
    switch(alignment) {
      case 'left':
        this.textTarget.classList.add('text-start');
        break;
      case 'center':
        this.textTarget.classList.add('text-center');
        break;
      case 'right':
        this.textTarget.classList.add('text-end');
        break;
      case 'justify':
        this.textTarget.classList.add('text-justify');
        break;
    }
  }

  edit() {
    this.textTarget.classList.add("collapse");
    this.editBtnTarget.classList.add("collapse");
    this.textTarget.classList.remove("d-inline-block");
    this.editBtnTarget.classList.remove("d-inline-block");

    this.allFieldsContainerTarget.classList.remove("collapse");
    this.editorContainerTarget.classList.add("d-flex");
    
    // Show the component when editing
    this.element.classList.remove('collapse');
    
    // this.editor.setText(this.textTarget.innerHTML);
    this.editor.root.innerHTML = this.textTarget.innerHTML.trim();
    
    // Restore alignment from existing classes
    this.restoreTextAlignment();
  }

  restoreTextAlignment() {
    if (this.textTarget.classList.contains('text-start')) {
      this.editor.format('align', 'left');
    } else if (this.textTarget.classList.contains('text-center')) {
      this.editor.format('align', 'center');
    } else if (this.textTarget.classList.contains('text-end')) {
      this.editor.format('align', 'right');
    } else if (this.textTarget.classList.contains('text-justify')) {
      this.editor.format('align', 'justify');
    }
  }

  save() {
    // const value = this.editor.getText().trim();
    const value = this.editor.root.innerHTML;

    // Get bottom margin value
    let btmMarginSelected = this.element.querySelector('.js-customize-mb:checked')?.value;
    if (!btmMarginSelected){
      btmMarginSelected = '0';
    }

    // Get top margin value
    let topMarginSelected = this.element.querySelector('.js-customize-mt:checked')?.value;
    if (!topMarginSelected){
      topMarginSelected = '0';
    }

    // Get start margin value (left in LTR, right in RTL)
    let startMarginSelected = this.element.querySelector('.js-customize-ms:checked')?.value;
    if (!startMarginSelected){
      startMarginSelected = '0';
    }

    // Get end margin value (right in LTR, left in RTL)
    let endMarginSelected = this.element.querySelector('.js-customize-me:checked')?.value;
    if (!endMarginSelected){
      endMarginSelected = '0';
    }

    // Get font size value
    let fontSizeSelected = this.element.querySelector('.js-customize-font-size')?.value || '';

    // Get progress text value - only if the field exists
    let progressTextSelected = '';
    const progressTextField = this.element.querySelector('.js-customize-progress-text');
    if (progressTextField) {
      progressTextSelected = progressTextField.value || '';
    }

    // Get video size value from dropdown
    let videoSizeSelected = this.element.querySelector('.js-customize-video-size')?.value;
    if (!videoSizeSelected){
      videoSizeSelected = '100%';
    }

    // Parse video size to extract width and height
    let videoWidthSelected = '100%';
    let videoHeightSelected = '500px';
    
    if (videoSizeSelected === '100%') {
      videoWidthSelected = '100%';
      videoHeightSelected = '500px';
    } else if (videoSizeSelected.includes('x')) {
      const [width, height] = videoSizeSelected.split('x');
      videoWidthSelected = width + 'px';
      videoHeightSelected = height + 'px';
    }

    this.textTarget.innerHTML = value;
    this.textTarget.classList.remove("collapse");
    this.editBtnTarget.classList.remove("collapse");
    this.textTarget.classList.add("d-inline-block");
    this.editBtnTarget.classList.add("d-inline-block");

    this.editorContainerTarget.classList.remove("d-flex");
    this.allFieldsContainerTarget.classList.add("collapse");
    
    // Show the component when text is present
    this.element.classList.remove('collapse');

    // Reset all margin classes
    this.resetMarginClasses();

    // Apply margin classes
    this.applyMarginClasses(btmMarginSelected, topMarginSelected, startMarginSelected, endMarginSelected);

    // Apply font size style
    this.applyFontSizeStyle(fontSizeSelected);

    // Apply current alignment
    const format = this.editor.getFormat();
    if (format.align) {
      this.applyTextAlignment(format.align);
    }

    $.ajax({
      url: `/view_preferences/customized_texts`,
      method: 'POST',
      data: { 
        key: this.textKey, 
        customized_text: { 
          value: value, 
          bottom_margin_value: btmMarginSelected,
          margin_top_value: topMarginSelected,
          margin_start_value: startMarginSelected,
          margin_end_value: endMarginSelected,
          font_size_value: fontSizeSelected,
          progress_text_value: progressTextSelected,
          video_size_value: videoSizeSelected
        } 
      },
      success: function(response) {
        alertCustom("Saved!", { displayPlacement: 'hover', alertType: 'success', icon: 'check' });
      },
      error: function() {
        alertCustom("Oops! Something went wrong while saving. Please try again.", { displayPlacement: 'hover' });
      }
    });
  }

  resetMarginClasses() {
    // Reset bottom margins
    this.element.classList.remove('mb-1', 'mb-2', 'mb-3', 'mb-4', 'mb-5');
    // Reset top margins
    this.element.classList.remove('mt-1', 'mt-2', 'mt-3', 'mt-4', 'mt-5');
    // Reset start margins (ms-*)
    this.element.classList.remove('ms-1', 'ms-2', 'ms-3', 'ms-4', 'ms-5');
    // Reset end margins (me-*)
    this.element.classList.remove('me-1', 'me-2', 'me-3', 'me-4', 'me-5');
  }

  applyMarginClasses(bottomMargin, topMargin, startMargin, endMargin) {
    // Apply bottom margin
    if (bottomMargin && bottomMargin !== '0') {
      this.element.classList.add(`mb-${bottomMargin}`);
    }
    
    // Apply top margin
    if (topMargin && topMargin !== '0') {
      this.element.classList.add(`mt-${topMargin}`);
    }
    
    // Apply start margin (ms-*)
    if (startMargin && startMargin !== '0') {
      this.element.classList.add(`ms-${startMargin}`);
    }
    
    // Apply end margin (me-*)
    if (endMargin && endMargin !== '0') {
      this.element.classList.add(`me-${endMargin}`);
    }
  }

  applyFontSizeStyle(fontSize) {
    if (fontSize && fontSize.trim() !== '') {
      // Remove existing font-size style
      this.element.style.removeProperty('font-size');
      
      // Add new font-size style
      let fontSizeStyle = '';
      if (fontSize.match(/^\d+$/)) {
        // Numeric value - assume pixels
        fontSizeStyle = `${fontSize}px`;
      } else if (fontSize.match(/^\d+px$/) || fontSize.match(/^\d+%$/) || fontSize.match(/^\d+rem$/) || fontSize.match(/^\d+em$/)) {
        // Already in proper format
        fontSizeStyle = fontSize;
      } else {
        // Default to pixels if format is unclear
        fontSizeStyle = `${fontSize}px`;
      }
      
      this.element.style.setProperty('font-size', fontSizeStyle, 'important');
    } else {
      // Remove font-size style if empty
      this.element.style.removeProperty('font-size');
    }
  }

  empty() {
    // this.textTarget.innerHTML = '';
    this.textTarget.classList.remove("collapse");
    this.editBtnTarget.classList.remove("collapse");
    this.textTarget.classList.add("d-inline-block");
    this.editBtnTarget.classList.add("d-inline-block");

    this.editorContainerTarget.classList.remove("d-flex");
    this.allFieldsContainerTarget.classList.add("collapse");
    
    // Hide the entire component when text is empty
    // this.element.classList.add('collapse');
  }

  reset() {
    this.textTarget.innerHTML = '';
    this.textTarget.classList.remove("collapse");
    this.editBtnTarget.classList.remove("collapse");
    this.textTarget.classList.add("d-inline-block");
    this.editBtnTarget.classList.add("d-inline-block");

    this.editorContainerTarget.classList.remove("d-flex");
    this.allFieldsContainerTarget.classList.add("collapse");
    
    // Hide the entire component when resetting
    // this.element.classList.add('collapse');

    // Reset all customizations
    this.resetMarginClasses();
    this.applyFontSizeStyle('');
    
    // Reset alignment
    this.textTarget.classList.remove('text-start', 'text-center', 'text-end', 'text-justify');

    // $.ajax({
    //   url: `/view_preferences/customized_texts/${this.textKey}`,
    //   method: 'DELETE',
    //   success: function(response) {
    //     alertCustom("Deleted! Refresh the page to view default text.", { displayPlacement: 'hover', alertType: 'success', icon: 'check' });
    //   },
    //   error: function() {
    //     alertCustom("Oops! Something went wrong while deleting. Please try again.", { displayPlacement: 'hover' });
    //   }
    // });

    $.ajax({
      url: `/view_preferences/customized_texts`,
      method: 'POST',
      data: { 
        key: this.textKey, 
        customized_text: { 
          value: '', 
          bottom_margin_value: '',
          margin_top_value: '',
          margin_start_value: '',
          margin_end_value: '',
          font_size_value: '',
          progress_text_value: '',
          video_size_value: ''
        } 
      },
      success: function(response) {
        alertCustom("Deleted!", { displayPlacement: 'hover', alertType: 'success', icon: 'check' });
      },
      error: function() {
        alertCustom("Oops! Something went wrong while deleting. Please try again.", { displayPlacement: 'hover' });
      }
    });

  }

  resetToDefault() {
    if (true || confirm('Are you sure you want to reset this text to its default value?')) {
      const textKey = this.element.dataset.key;
      
      // Send AJAX request to delete the customized text record
      fetch(`/view_preferences/customized_texts/${textKey}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
          'Content-Type': 'application/json',
        },
      })
      .then(response => {
        if (response.ok) {
          alertCustom("Successfully reset to default text.", { displayPlacement: 'hover', alertType: 'success', icon: 'check' });
        } else {
          if (response.status == 404){
            alertCustom('Successfully reset to default text. However, no customized value was found for this.', { displayPlacement: 'hover', alertType: 'info' });
          } else {
            alertCustom('Failed to reset to default. Please try again.', { displayPlacement: 'hover' });
          }
        }
      })
      .catch(error => {
        alertCustom("Error resetting to default. Please try again.", { displayPlacement: 'hover' });
      });
    }
  }
}
